import { useState, useEffect, useRef } from 'react';
import { Menu, X, Mail, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { SiFacebook, SiLinkedin, SiX } from 'react-icons/si';
import { NAV_LINKS } from '@/lib/index';

/* ── 浅色工业风色值（对齐宣传册） ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const DEEP_BG = '#0F1E35';          /* 深蓝页脚/顶栏 */
const BORDER = '#D0D5E2';
const FOOTER_BORDER = '#1E3050';

export function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    if (headerRef.current) ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  /* 判断当前路由是否激活 */
  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Fixed Header ── */}
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: isScrolled
            ? 'rgba(255,255,255,0.99)'
            : 'rgba(255,255,255,0.97)',
          boxShadow: isScrolled ? '0 2px 16px rgba(26,35,55,0.12)' : 'none',
        }}
      >
        {/* Top Contact Bar */}
        <div style={{ background: DEEP_BG, borderBottom: `1px solid ${FOOTER_BORDER}` }}>
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between py-2 text-xs">
              <span
                className="hidden md:block font-mono tracking-widest uppercase text-[10px]"
                style={{ color: ORANGE }}
              >
                博元合成（北京）工业科技有限公司 · POLY-PRIME INDUSTRIAL TECHNOLOGY
              </span>
              <div className="flex items-center gap-6 ml-auto">
                {/* 隐蔽管理入口：小橙色 P 图标，点击跳转 /login */}
                <Link
                  to="/login"
                  title="管理后台"
                  className="opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
                  style={{ lineHeight: 0 }}
                >
                  <span
                    className="w-4 h-4 rounded-[3px] flex items-center justify-center text-white font-black"
                    style={{ background: ORANGE, fontSize: '9px' }}
                  >P</span>
                </Link>
                <a href="mailto:info@poly-prime.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Mail className="w-3.5 h-3.5" />
                  <span>info@poly-prime.com</span>
                </a>
                <a href="tel:13426031477" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Phone className="w-3.5 h-3.5" />
                  <span>13426031477</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Nav */}
        <div style={{ borderBottom: `1px solid ${BORDER}` }}>
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2 flex-shrink-0">
                <div
                  className="w-7 h-7 rounded-sm flex items-center justify-center"
                  style={{ background: ORANGE }}
                >
                  <span className="text-xs font-black text-white">P</span>
                </div>
                <div className="text-xl font-black tracking-wider">
                  <span style={{ color: ORANGE }}>POLY</span>
                  <span className="text-foreground">-PRIME</span>
                </div>
                <div className="hidden xl:block h-5 w-px mx-1" style={{ background: BORDER }} />
                <span className="hidden xl:block text-xs text-muted-foreground tracking-wider">
                  特种工业橡胶解决方案
                </span>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-0.5">
                {NAV_LINKS.map((link) => {
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={link.id}
                      to={link.href}
                      className="group relative flex flex-col items-center px-3 py-2 rounded transition-colors"
                      style={{ background: active ? 'oklch(0.66 0.19 42 / 0.1)' : 'transparent' }}
                    >
                      <span
                        className="text-[9px] font-bold tracking-[0.2em] uppercase mb-0.5 transition-colors"
                        style={{ color: active ? ORANGE : '#7A8899' }}
                      >
                        {link.labelEn}
                      </span>
                      <span
                        className="text-sm font-medium transition-colors"
                        style={{ color: active ? ORANGE : '#1A2337' }}
                      >
                        {link.label}
                      </span>
                      <span
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded transition-all duration-200"
                        style={{
                          background: ORANGE,
                          width: active ? '70%' : '0%',
                        }}
                      />
                    </Link>
                  );
                })}
              </nav>

              {/* CTA + Mobile Toggle */}
              <div className="flex items-center gap-3">
                <Link
                  to="/contact"
                  className="hidden lg:inline-flex items-center gap-2 px-5 py-2 text-sm font-bold rounded-sm transition-all"
                  style={{ background: ORANGE, color: '#FFFFFF' }}
                >
                  立即询价
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border" style={{ background: '#FFFFFF' }}>
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.id}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-3 px-3 rounded border-b border-border/40 last:border-0 transition-colors"
                    style={{ background: active ? 'oklch(0.66 0.19 42 / 0.08)' : 'transparent' }}
                  >
                    <span
                      className="text-[10px] font-bold tracking-widest uppercase w-20 flex-shrink-0"
                      style={{ color: ORANGE }}
                    >
                      {link.labelEn}
                    </span>
                    <span className="text-sm font-medium text-foreground">{link.label}</span>
                  </Link>
                );
              })}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-2 w-full flex items-center justify-center py-3 text-sm font-bold rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                立即询价
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main style={{ paddingTop: `${headerHeight}px` }}>{children}</main>

      {/* ── Footer ── */}
      <footer style={{ background: DEEP_BG, borderTop: `1px solid ${FOOTER_BORDER}` }}>
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: ORANGE }}>
                  <span className="text-sm font-black text-white">P</span>
                </div>
                <div className="text-xl font-black tracking-wider">
                  <span style={{ color: ORANGE }}>POLY</span>
                  <span style={{ color: '#FFFFFF' }}>-PRIME</span>
                </div>
              </div>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: '#9DAFC6' }}>
                专注特种工业橡胶密封材料研发与制造，FKM/EPDM/NBR等高性能材料，服务全球化工、能源、轨道交通等重工业领域。
              </p>
              <div className="flex items-center gap-3">
                {[
                  { Icon: SiFacebook, href: 'https://facebook.com', label: 'Facebook' },
                  { Icon: SiLinkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
                  { Icon: SiX, href: 'https://x.com', label: 'X' },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-9 h-9 rounded flex items-center justify-center transition-colors"
                    style={{ border: `1px solid ${FOOTER_BORDER}`, color: '#9DAFC6' }}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Nav */}
            <div>
              <h3 className="text-xs font-bold tracking-[0.15em] uppercase mb-6" style={{ color: ORANGE }}>
                快速导航
              </h3>
              <ul className="space-y-2.5">
                {NAV_LINKS.map((link) => (
                  <li key={link.id}>
                    <Link
                      to={link.href}
                      className="text-sm hover:text-primary transition-colors flex items-center gap-2" style={{ color: '#9DAFC6' }}
                    >
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <h3 className="text-xs font-bold tracking-[0.15em] uppercase mb-6" style={{ color: ORANGE }}>
                产品系列
              </h3>
              <ul className="space-y-2.5">
                {([
                  { label: 'FKM氟橡胶板', to: '/products/fkm' },
                  { label: 'EPDM三元乙丙橡胶板', to: '/products/epdm' },
                  { label: 'NBR丁腈橡胶板', to: '/products/nbr' },
                  { label: 'NR天然橡胶板', to: '/products/nr' },
                  { label: 'CR氯丁橡胶板', to: '/products/cr' },
                  { label: '抗静电橡胶板', to: '/products/esd' },
                ] as { label: string; to: string }[]).map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      className="text-sm hover:text-primary transition-colors flex items-center gap-2" style={{ color: '#9DAFC6' }}
                    >
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xs font-bold tracking-[0.15em] uppercase mb-6" style={{ color: ORANGE }}>
                联系我们
              </h3>
              <address className="not-italic space-y-4 text-sm" style={{ color: '#9DAFC6' }}>
                <div>
                  <p className="font-semibold text-xs tracking-wider uppercase mb-0.5" style={{ color: '#FFFFFF' }}>北京总部</p>
                  <p>北京市朝阳区</p>
                </div>
                <div>
                  <p className="font-semibold text-xs tracking-wider uppercase mb-0.5" style={{ color: '#FFFFFF' }}>制造基地</p>
                  <p>河北省沧州市橡胶产业基地</p>
                </div>
                <div>
                  <p className="font-semibold text-xs tracking-wider uppercase mb-0.5" style={{ color: '#FFFFFF' }}>联系电话</p>
                  <a href="tel:13426031477" className="hover:text-primary transition-colors font-mono">13426031477</a>
                </div>
                <div>
                  <p className="font-semibold text-xs tracking-wider uppercase mb-0.5" style={{ color: '#FFFFFF' }}>电子邮箱</p>
                  <a href="mailto:info@poly-prime.com" className="hover:text-primary transition-colors">info@poly-prime.com</a>
                </div>
              </address>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{ borderTop: `1px solid ${FOOTER_BORDER}` }}>
          <div className="container mx-auto px-4 py-5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs" style={{ color: '#6A7F96' }}>
              <p>© 2026 博元合成（北京）工业科技有限公司 · POLY-PRIME INDUSTRIAL TECHNOLOGY · 保留所有权利</p>
              <div className="flex items-center gap-3">
                {['ISO 9001', 'GDPR', 'CE认证'].map((badge) => (
                  <span
                    key={badge}
                    className="px-2.5 py-1 rounded text-[10px] font-bold tracking-widest uppercase"
                    style={{
                      color: ORANGE,
                      background: 'oklch(0.66 0.19 42 / 0.1)',
                      border: '1px solid oklch(0.66 0.19 42 / 0.3)',
                    }}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
